import { useEffect, useRef, useState } from 'react'
import { selectQuery, useAppStore } from '../../state/store'

/** Keystrokes settle for this long before the query reaches the store and the URL. */
export const SEARCH_DEBOUNCE_MS = 250

/**
 * A text input bound to `store.query`, debounced.
 *
 * The input keeps its own value so typing stays at 60fps, and pushes to the
 * store once the user pauses - which is also what writes the history entry, so
 * a sentence typed into the box costs one back press, not one per letter.
 *
 * External changes to `query` (a popstate, clear-all, a pasted link) are adopted
 * back into the input, without clobbering what is being typed right now.
 */
export const useDebouncedQuery = (
  delayMs: number = SEARCH_DEBOUNCE_MS,
): { value: string; setValue: (value: string) => void } => {
  const query = useAppStore(selectQuery)
  const setQuery = useAppStore((state) => state.setQuery)
  const [value, setValue] = useState(query)
  /** The last value this hook handed to the store, in both directions. */
  const settled = useRef(query)

  useEffect(() => {
    if (query === settled.current) return
    settled.current = query
    setValue(query)
  }, [query])

  useEffect(() => {
    if (value === settled.current) return
    const timer = window.setTimeout(() => {
      settled.current = value
      setQuery(value)
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [value, delayMs, setQuery])

  return { value, setValue }
}
